import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface Rejected {
  wanLinkId: string;
  name: string;
  reason: string;
  detail: string;
}

interface Selection {
  trafficClass: string;
  chosen: { wanLinkId: string; name: string } | null;
  alternates: Array<{ wanLinkId: string; name: string }>;
  rejected: Rejected[];
  reason: string;
  compromised: boolean;
  warnings: string[];
}

/**
 * Which WAN carries which class of traffic.
 *
 * Each class shows what it is on, why, and what it would fall back to. The
 * "not being carried" case is stated rather than left as an absence: traffic
 * quietly leaving by a route nobody chose is worse than traffic that stops,
 * because nobody finds out until it matters.
 */
export function PathSelectionPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<Selection[]>(`/wan/devices/${deviceId}/paths`),
    [deviceId],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data || data.length === 0) {
    return (
      <div className="card">
        <h2>{t('pathSelection.title')}</h2>
        <EmptyState hint={t('pathSelection.empty')} />
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('pathSelection.title')}</h2>

      {data.map((selection) => (
        <div className="card card--nested" key={selection.trafficClass}>
          <div className="page-head">
            <h3>{t(`pathSelection.class.${selection.trafficClass}`)}</h3>
            {selection.chosen ? (
              <span className={`chip chip--${selection.compromised ? 'warning' : 'ok'}`}>
                {t('pathSelection.chosen')}: {selection.chosen.name}
              </span>
            ) : (
              <span className="chip chip--error">{t('pathSelection.noPath')}</span>
            )}
          </div>

          <p className="muted">{selection.reason}</p>

          {selection.chosen === null ? (
            <Banner variant="error">{t('pathSelection.noPathHint')}</Banner>
          ) : null}
          {selection.compromised ? (
            <Banner variant="warning">{t('pathSelection.compromised')}</Banner>
          ) : null}
          {selection.warnings.map((w) => (
            <p className="muted" key={w}>
              {w}
            </p>
          ))}

          {selection.alternates.length > 0 ? (
            <p>
              {t('pathSelection.alternates')}: {selection.alternates.map((x) => x.name).join(', ')}
            </p>
          ) : null}

          {selection.rejected.length > 0 ? (
            <>
              <h4>{t('pathSelection.rejected')}</h4>
              <ul className="muted">
                {selection.rejected.map((r) => (
                  <li key={r.wanLinkId}>
                    <code>{r.name}</code> — {t(`pathSelection.reason.${r.reason}`)}: {r.detail}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}
